import React, { useEffect, useState } from 'react';
import {
  DetailOption,
  QlfFilterDetails,
  QlfMasterDetail,
  QlfSimpleDetail,
} from '@qualifio/ui-commons/dist/components/qlf-master-detail';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfConfirmationDialog } from '@qualifio/ui-commons';
import { Skeleton } from '@mui/material';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { useTranslation } from 'react-i18next';
import { InteractionDetail } from '../../libs/interactions/components/detail/InteractionDetail';
import { InteractionFormValues } from '../../libs/interactions/components/detail/InteractionForm';
import { Page, PageBody, PageHeader } from '../../libs/shared/page';
import { QlfMasterDetailContainer } from '../../libs/shared/layout/QlfMasterDetailContainer';
import { QlfPageErrorBoundary } from '../../libs/shared/QlfPageErrorBoundary';
import { TopBar } from '../../libs/shared/layout/TopBar';
import { useConfirmDialog } from '../../libs/shared/use-confirm-dialog';
import { useFeatures } from '../../libs/features/FeaturesContext';
import { useInteractionsContext } from '../../libs/interactions/InteractionsContext';

export function InteractionsIndexPage() {
  const { t } = useTranslation();
  const [searchField, setSearchField] = useState('');
  const { features } = useFeatures();
  const { remove: interactionRemove, write: interactionWrite } = features[Feature.INTERACTION];
  const { askConfirmation, props, setProps, ...dialogProps } = useConfirmDialog();

  const {
    selectedInteraction,
    setSelectedInteraction,
    getInteractions,
    interactions,
    isLoading,
    deleteInteraction,
    createInteraction,
  } = useInteractionsContext();

  useEffect(() => {
    return () => {
      setSelectedInteraction(undefined);
    };
  }, [setSelectedInteraction]);

  useEffect(() => {
    getInteractions();
  }, [getInteractions]);

  const templateHeader = () => (
    <QlfFilterDetails
      placeholder={t('interactions.sidebar.search')}
      onAdd={() => createInteraction()}
      disableAdd={!interactionWrite}
      onChange={e => setSearchField(e.target.value)}
    />
  );

  const templateRow = (rowProps: { data: InteractionFormValues }) => {
    const { data: interaction } = rowProps;

    const options: DetailOption[] = [
      {
        node: <p>{t('master-detail.actions.remove')}</p>,
        startIcon: <FontAwesomeIcon fixedWidth={true} icon={faTrash} />,
        callback: () => {
          setProps({ interactionName: interaction?.name });
          askConfirmation(() => {
            if (interaction?.id) {
              deleteInteraction(interaction.id);
            }
          });
        },
        disabled: !interactionRemove,
        tooltip: interactionRemove ? '' : t('shared.action.permission.required'),
      },
    ];

    return (
      <QlfSimpleDetail
        state={interaction.id === selectedInteraction?.id ? 'active' : 'default'}
        options={options}
        callback={() => setSelectedInteraction(interaction)}
        content={interaction.name || t('interactions.states.unnamed')}
      />
    );
  };

  const items = interactions.filter(
    interaction =>
      !searchField || interaction.name.toLowerCase().includes(searchField.toLowerCase()),
  );

  return (
    <Page>
      <PageHeader>
        <TopBar />
      </PageHeader>
      <PageBody>
        <QlfPageErrorBoundary>
          {isLoading('getInteractions') ? (
            <Skeleton />
          ) : (
            <>
              <QlfMasterDetailContainer isAccountLevel>
                <QlfMasterDetail
                  templateHeader={templateHeader}
                  templateRow={templateRow}
                  templateDetail={InteractionDetail}
                  items={items}
                />
              </QlfMasterDetailContainer>
              <QlfConfirmationDialog
                {...dialogProps}
                title={t('interactions.confirm-delete.title')}
                content={t('interactions.confirm-delete.message', {
                  name: props?.interactionName,
                })}
                color="warning"
                confirmButtonText={t('generic.actions.hard-delete')}
                cancelButtonText={t('generic.actions.cancel')}
              />
            </>
          )}
        </QlfPageErrorBoundary>
      </PageBody>
    </Page>
  );
}
